/* eslint complexity: 0 */
import { get_template_profile } from './workflows.template_profiles.js';
import { get_workflow_profile } from './workflows.workflow_profiles.js';
import {
  workflow_approvals_for,
  workflow_audit_trail_for,
  workflow_connects_to_for,
  workflow_default_details,
  workflow_default_footer_actions,
  workflow_escalation_for,
  workflow_evidence_for,
  workflow_governing_rule_items,
  workflow_stage_meta_for,
  workflow_stage_summary_for,
  workflow_status_class_for,
  workflow_status_label_for,
  workflow_timeline_for
} from './workflows.catalog_helpers.js';

export const start_workflow_templates = [
  { id: 'supplier_onboarding', label: 'Supplier onboarding (OnboardOS)', owner: 'Procurement', risk: { label: 'Medium risk', class: 'bg-warning-subtle text-warning-emphasis' }, policy: 'POL-SCM Procurement policy', control: 'C-101 No supplier paid without valid tax, banking & contract', stage_count: 6, connection_count: 6, note: 'This is an OnboardOS journey - the instance runs in OnboardOS and hands the activated record back to Complyon on completion.' },
  { id: 'employee_onboarding', label: 'Employee onboarding (OnboardOS)', owner: 'HR', risk: { label: 'Low risk', class: 'bg-success-subtle text-success-emphasis' }, policy: 'POL-HR Employee policy', control: 'C-203 No access without signed declarations & training', stage_count: 5, connection_count: 5, note: 'This is an OnboardOS journey - the instance runs in OnboardOS and hands the activated record back to Complyon on completion.' },
  { id: 'policy_approval', label: 'Policy approval', owner: 'Compliance', risk: { label: 'Medium risk', class: 'bg-warning-subtle text-warning-emphasis' }, policy: 'POL-META Governance policy', control: 'C-501 Approval before publication', stage_count: 6, connection_count: 4, note: 'Native Complyon workflow for drafting, legal review and board approval.' },
  { id: 'incident_management', label: 'Incident management', owner: 'Risk', risk: { label: 'High risk', class: 'bg-danger-subtle text-danger-emphasis' }, policy: 'POL-IRM Incident response policy', control: 'C-216 Incident triage and closure', stage_count: 5, connection_count: 5, note: 'Native Complyon workflow for triage, investigation, corrective action and closure.' }
];

export const workflows_computed = {
  active_drawer() {
    const drawer = this.active_workflow_item
      ? this.build_workflow_drawer(this.active_workflow_item)
      : this.drawer_catalog[this.active_drawer_key] || null;
    return drawer;
  },
  active_drawer_footer_primary_actions() {
    const actions = this.active_drawer?.footer_actions || [];
    const has_positions = actions.some((action) => action.position);
    const primary_actions = has_positions ? actions.filter((action) => action.position === 'left') : [];
    return primary_actions;
  },
  active_drawer_footer_actions() {
    const actions = this.active_drawer?.footer_actions || [];
    const has_positions = actions.some((action) => action.position);
    const footer_actions = has_positions ? actions.filter((action) => action.position !== 'left') : actions;
    return footer_actions;
  },
  active_start_workflow_template() {
    const template = this.start_workflow_templates.find((item) => item.id === this.start_workflow_form.template_id) || this.start_workflow_templates[0];
    return template;
  }
};

function scroll_drawer_top(vm) {
  vm.$nextTick(() => {
    const drawer_body = vm.$refs.drawerBody;
    if (drawer_body) drawer_body.scrollTop = 0;
  });
}

function workflow_section_label(workflow) {
  const identifier = String(workflow?.id || workflow?.name || 'WORKFLOW').toUpperCase();
  const type_label = String(workflow?.type_label || workflow?.type || workflow?.name || 'Workflow').toUpperCase();
  return `WORKFLOW Â· ${identifier} Â· ${type_label}`;
}

function workflow_owner_text(workflow) {
  const orchestration = workflow?.type_code === 'ONBOARDS' ? 'OnboardOS journey' : 'Complyon orchestration';
  return `Owner ${workflow?.owner || 'Compliance'} Â· ${orchestration}`;
}

function workflow_sections(context, workflow, profile, stage_summary, current_stage, stage_count) {
  return [
    { title: 'Current stage', badge: stage_summary.badge, badge_class: 'bg-success-subtle text-success-emphasis', kind: 'stage', current: current_stage, stage_meta: profile?.stage_meta || workflow_stage_meta_for(workflow) },
    { title: 'Governing rule', badge: workflow?.risk?.label || 'Linked', badge_class: workflow?.risk?.class || 'bg-warning-subtle text-warning-emphasis', kind: 'items', items: profile?.governing_rule_items || workflow_governing_rule_items(workflow) },
    { title: 'Stages', badge: `${stage_count || 1} stages`, badge_class: 'bg-light text-secondary border', kind: 'timeline', items: profile?.timeline || context.workflow_timeline(workflow) },
    { title: 'Evidence required Â· this stage', badge: 'Open vault', badge_class: 'bg-light text-secondary border', kind: 'items', items: profile?.evidence_items || context.workflow_evidence_items(workflow) },
    { title: 'Approvals on this workflow', badge: workflow?.id === 'wf-230' ? 'Pending' : '2 pending', badge_class: 'bg-warning-subtle text-warning-emphasis', kind: 'items', items: profile?.approvals || context.workflow_approvals(workflow) },
    { title: 'Escalation path', badge: 'Triggered', badge_class: 'bg-danger-subtle text-danger-emphasis', kind: 'chips', items: profile?.escalation_path || context.workflow_escalation_path(workflow) },
    { title: 'Connects to', badge: '', badge_class: '', kind: 'chips', items: profile?.connects_to || context.workflow_connects_to(workflow) },
    { title: 'Audit trail', badge: '', badge_class: '', kind: 'timeline', items: profile?.audit_trail || context.workflow_audit_trail(workflow) }
  ];
}

function workflow_drawer_payload(context, workflow, profile) {
  const stage_count = context.workflow_stage_count(workflow);
  const stage_summary = profile?.stage_summary || context.workflow_stage_summary(workflow);
  const current_stage = stage_summary.current;
  const status_label = profile?.status || context.workflow_status_label(workflow);
  const status_class = profile?.status_class || context.workflow_status_class(workflow);
  const drawer = {
    section: workflow_section_label(workflow),
    title: profile?.title || workflow?.name || 'Workflow',
    subtitle: profile?.subtitle || workflow_owner_text(workflow),
    status: status_label,
    status_class,
    details_layout: profile?.details_layout || 'list',
    details: profile?.details || workflow_default_details(workflow, current_stage, stage_count),
    sections: workflow_sections(context, workflow, profile, stage_summary, current_stage, stage_count),
    footer_actions: profile?.footer_actions || workflow_default_footer_actions(workflow)
  };
  return drawer;
}

export const workflows_methods = {
  set_tab(tab_id) { this.active_tab = tab_id; },
  workflow_status_label(workflow) {
    const label = workflow_status_label_for(workflow);
    return label;
  },
  workflow_status_class(workflow) {
    const status = this.workflow_status_label(workflow);
    const status_class = workflow_status_class_for(status);
    return status_class;
  },
  workflow_current_stage(workflow) { return workflow?.stage || workflow?.stages?.[1] || workflow?.stages?.[0] || 'Draft'; },
  workflow_stage_count(workflow) { return workflow?.stage_count || workflow?.stages?.length || 0; },
  workflow_connection_count(workflow) { return workflow?.connection_count || workflow?.stages?.length || 0; },
  workflow_header_label(workflow) {
    const header_label = workflow_section_label(workflow);
    return header_label;
  },
  workflow_owner_label(workflow) {
    const owner_label = workflow_owner_text(workflow);
    return owner_label;
  },
  workflow_stage_summary(workflow) {
    const summary = workflow_stage_summary_for(workflow, {
      current_stage: this.workflow_current_stage.bind(this),
      stage_count: this.workflow_stage_count.bind(this)
    });
    return summary;
  },
  workflow_evidence_items(workflow) {
    const evidence_items = workflow_evidence_for(workflow);
    return evidence_items;
  },
  workflow_approvals(workflow) {
    const approvals = workflow_approvals_for(workflow);
    return approvals;
  },
  workflow_timeline(workflow) {
    const timeline = workflow_timeline_for(workflow);
    return timeline;
  },
  workflow_audit_trail(workflow) {
    const audit_trail = workflow_audit_trail_for(workflow, {
      current_stage: this.workflow_current_stage.bind(this),
      status_label: this.workflow_status_label.bind(this)
    });
    return audit_trail;
  },
  workflow_connects_to(workflow) {
    const connected_items = workflow_connects_to_for(workflow);
    return connected_items;
  },
  workflow_escalation_path(workflow) {
    const escalation_path = workflow_escalation_for(workflow);
    return escalation_path;
  },
  workflow_drawer_profile(workflow) { return get_workflow_profile(workflow, this); },
  template_drawer_profile(template) { return get_template_profile(template); },
  build_workflow_drawer(workflow) {
    if (!workflow?.id) {
      const template_drawer = this.template_drawer_profile(workflow) || this.drawer_catalog[workflow?.drawer_key] || null;
      return template_drawer;
    }
    const profile = this.workflow_drawer_profile(workflow);
    const drawer = workflow_drawer_payload(this, workflow, profile);
    return drawer;
  },
  open_start_workflow_modal() { this.show_start_workflow_modal = true; },
  close_start_workflow_modal() { this.show_start_workflow_modal = false; },
  start_workflow() { this.close_start_workflow_modal(); },
  open_workflow_drawer(workflow) { if (!workflow) return; this.active_workflow_item = workflow; this.active_drawer_key = workflow.drawer_key || workflow.id || null; scroll_drawer_top(this); },
  open_drawer(drawer_key) { if (!drawer_key) return; this.active_workflow_item = null; this.active_drawer_key = drawer_key; scroll_drawer_top(this); },
  close_drawer() { this.active_drawer_key = null; this.active_workflow_item = null; }
};
